import { api } from '@/lib/api'
import { API_PATHS } from '@/constants/api-paths'
import type { ApiResponse } from '@/types'

const EVENT_PATHS = API_PATHS.events

// --- Workday types ---

export interface EventWorkerCheckinStatus {
  worker_id: string | number
  worker_name: string
  checked_in: boolean
  checked_in_at: string | null
  checked_out_at: string | null
  hours: number | null
}

export interface EventWorkdaySyncResult {
  created: number
  updated: number
  failed: number
  errors?: string[]
}

// --- Payment types ---

export interface EventFinancialSnapshot {
  total_amount: number
  paid_amount: number
  pending_amount: number
  workers_count: number
}

export interface EventWorkerPaymentStatus {
  worker_id: string | number
  worker_name: string
  amount: number
  status: string
  invoice_id: string | number | null
}

export interface EventInvoiceGenerateResult {
  created: number
  updated: number
  failed: number
}

export interface EventPaymentReconciliation {
  total_invoiced: number
  total_paid: number
  total_pending: number
  total_overdue: number
  workers_count: number
  paid_count: number
  pending_count: number
}

// --- Invoice types ---

export interface EventInvoice {
  id: string | number
  assignment_id: string | number | null
  worker_id: string | number | null
  worker_name: string | null
  amount: number
  paid_amount: number | null
  status: string
  issued_at: string | null
  paid_at: string | null
  days_outstanding?: number | null
}

export interface EventInvoiceSummary {
  total: number
  total_amount: number
  paid_count: number
  paid_amount: number
  pending_count: number
  pending_amount: number
  overdue_count: number
}

// --- Workflow types ---

export interface EventWorkflowTransition {
  id: string | number
  to_status_id: string | number
  to_status_code: string
  to_status_label: string
}

export interface EventWorkflowStatus {
  workflow_status_id: string | number | null
  workflow_status_code: string | null
  workflow_status_label: string | null
  available_transitions: EventWorkflowTransition[]
}

export interface EventWorkflowTransitionResult {
  event_id: string | number
  job_id: string | number
  previous_status_code: string | null
  new_status_id: string | number
  new_status_code: string
  new_status_label: string
}

// --- API client ---

export const eventsApi = {
  workday: {
    ensureEntries: async (jobId: string | number): Promise<ApiResponse<unknown>> => {
      const response = await api.post<ApiResponse<unknown>>(EVENT_PATHS.workday.ensureEntries(jobId))
      return response.data
    },

    syncWorkers: async (jobId: string | number): Promise<EventWorkdaySyncResult> => {
      const response = await api.post<ApiResponse<EventWorkdaySyncResult>>(EVENT_PATHS.workday.syncWorkers(jobId))
      return response.data.data
    },

    checkinStatus: async (jobId: string | number): Promise<EventWorkerCheckinStatus[]> => {
      const response = await api.get<ApiResponse<EventWorkerCheckinStatus[]>>(EVENT_PATHS.workday.checkinStatus(jobId))
      return response.data.data ?? []
    },

    checkin: async (jobId: string | number, workerId: string | number, checkinTime?: string): Promise<boolean> => {
      const response = await api.post<ApiResponse<{ success: boolean }>>(
        EVENT_PATHS.workday.checkin(jobId),
        { worker_id: workerId, ...(checkinTime ? { checkin_time: checkinTime } : {}) }
      )
      return response.data.data?.success ?? false
    },

    checkout: async (jobId: string | number, workerId: string | number, checkoutTime?: string): Promise<boolean> => {
      const response = await api.post<ApiResponse<{ success: boolean }>>(
        EVENT_PATHS.workday.checkout(jobId),
        { worker_id: workerId, ...(checkoutTime ? { checkout_time: checkoutTime } : {}) }
      )
      return response.data.data?.success ?? false
    },
  },

  payment: {
    snapshot: async (jobId: string | number): Promise<EventFinancialSnapshot> => {
      const response = await api.get<ApiResponse<EventFinancialSnapshot>>(EVENT_PATHS.payment.snapshot(jobId))
      return response.data.data
    },

    workerStatus: async (jobId: string | number): Promise<EventWorkerPaymentStatus[]> => {
      const response = await api.get<ApiResponse<EventWorkerPaymentStatus[]>>(EVENT_PATHS.payment.workerStatus(jobId))
      return response.data.data ?? []
    },

    generateInvoices: async (jobId: string | number): Promise<EventInvoiceGenerateResult> => {
      const response = await api.post<ApiResponse<EventInvoiceGenerateResult>>(EVENT_PATHS.payment.generateInvoices(jobId))
      return response.data.data
    },

    processWorker: async (
      jobId: string | number,
      workerId: string | number,
      amount: number,
      paymentMethod?: string
    ): Promise<boolean> => {
      const response = await api.post<ApiResponse<{ success: boolean }>>(
        EVENT_PATHS.payment.processWorker(jobId),
        { worker_id: workerId, amount, ...(paymentMethod ? { payment_method: paymentMethod } : {}) }
      )
      return response.data.data?.success ?? false
    },

    reconcile: async (jobId: string | number): Promise<EventPaymentReconciliation> => {
      const response = await api.post<ApiResponse<EventPaymentReconciliation>>(EVENT_PATHS.payment.reconcile(jobId))
      return response.data.data
    },
  },

  invoices: {
    list: async (jobId: string | number): Promise<EventInvoice[]> => {
      const response = await api.get<ApiResponse<EventInvoice[]>>(EVENT_PATHS.invoices.list(jobId))
      return response.data.data ?? []
    },

    unpaid: async (jobId: string | number): Promise<EventInvoice[]> => {
      const response = await api.get<ApiResponse<EventInvoice[]>>(EVENT_PATHS.invoices.unpaid(jobId))
      return response.data.data ?? []
    },

    summary: async (jobId: string | number): Promise<EventInvoiceSummary> => {
      const response = await api.get<ApiResponse<EventInvoiceSummary>>(EVENT_PATHS.invoices.summary(jobId))
      return response.data.data
    },

    finalize: async (jobId: string | number, invoiceId: string | number): Promise<boolean> => {
      const response = await api.post<ApiResponse<{ success: boolean }>>(
        EVENT_PATHS.invoices.finalize(jobId, invoiceId)
      )
      return response.data.data?.success ?? false
    },

    markPaid: async (jobId: string | number, invoiceId: string | number, paidAmount?: number): Promise<boolean> => {
      const response = await api.post<ApiResponse<{ success: boolean }>>(
        EVENT_PATHS.invoices.markPaid(jobId, invoiceId),
        paidAmount !== undefined ? { paid_amount: paidAmount } : {}
      )
      return response.data.data?.success ?? false
    },
  },

  workflow: {
    currentStatus: async (jobId: string | number): Promise<EventWorkflowStatus> => {
      const response = await api.get<ApiResponse<EventWorkflowStatus>>(EVENT_PATHS.workflow.currentStatus(jobId))
      return response.data.data
    },

    applyStatus: async (jobId: string | number, toStatusId: string | number, note?: string): Promise<EventWorkflowTransitionResult> => {
      const response = await api.post<ApiResponse<EventWorkflowTransitionResult>>(
        EVENT_PATHS.workflow.applyStatus(jobId),
        { to_status_id: toStatusId, ...(note ? { note } : {}) }
      )
      return response.data.data
    },
  },
}

export default eventsApi
